import { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import EditableTagCell from "./EditableTagCell"
import {
  getAssignedCrew,
  getAvailableStaff,
  assignMultipleStaff,
  unassignStaffMember,
  selectAssignedCrew,
  selectUnassignOperationLoading
} from "store/staffAssignments"
import styles from "./BookingTable.module.css"

const renderCrewLabel = (member) => {
  const roleSuffix = member.role ? ` (${member.role})` : ""
  return `${member.name}${roleSuffix}`
}

// filter=all returns every crew member across availability buckets; flatten them into one
// list, tagging each with the bucket it came from so the picker can surface a
// Day Off / Booking Conflict badge (see toCrewItem).
const withStatus = (list, status) => (list || []).map((member) => ({ ...member, status: member.status || status }))

const normalizeStaffList = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === "object") {
    return [...withStatus(data.available, "available"), ...withStatus(data.unavailable, "unavailable"), ...withStatus(data.has_bookings, "has_bookings")]
  }
  return []
}

const toCrewItem = (staff) => {
  const primaryRole = staff.role || staff.primary_role || staff.roles?.[0]
  return {
    id: staff.id,
    name: staff.full_name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim(),
    role: typeof primaryRole === "object" ? primaryRole?.name || "" : primaryRole || "",
    status: staff.status
  }
}

// A crew member with an overlapping booking (is_booking_conflict) can't be assigned; one on a
// day off is flagged but still assignable (mirrors the old booking-detail crew tab). We read
// the per-member is_booking_conflict flag directly rather than inferring it from the bucket.
const decorateCrewItem = (staff, t) => {
  const item = toCrewItem(staff)
  if (staff.is_booking_conflict) {
    return { ...item, badge: { text: t("booking.crew.status_booking_conflict", { defaultValue: "Booking Conflict" }), color: "orange" }, disabled: true }
  }
  if (item.status === "unavailable") {
    return { ...item, badge: { text: t("booking.crew.status_day_off", { defaultValue: "Day Off" }), color: "red" }, disabled: false }
  }
  return { ...item, badge: null, disabled: false }
}

const CrewCell = ({ initialCrew, bookingId, emptyLabel, alwaysEditable, onCountChange }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const assignedCrew = useSelector(selectAssignedCrew)
  const removeLoading = useSelector(selectUnassignOperationLoading)
  const [groups, setGroups] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleEditStart = () => {
    if (bookingId) dispatch(getAssignedCrew(bookingId))
  }

  const handlePickerOpen = () => {
    if (!bookingId) return
    // filter=all returns three availability buckets; the bucket only drives each member's
    // badge/disabled state (see decorateCrewItem). Grouping stays keyed on is_assigned —
    // assigned crew under "Available", the rest under "Other Crew" — matching the old booking
    // detail, where a member with a booking conflict is listed but disabled from re-assigning.
    setIsLoading(true)
    dispatch(getAvailableStaff(bookingId, { filter: "all" }))
      .then((res) => {
        const staff = normalizeStaffList(res?.data)
        const decorate = (member) => decorateCrewItem(member, t)
        setGroups([
          { key: "available", label: "Available", items: staff.filter((member) => member.is_assigned).map(decorate) },
          { key: "others", label: "Other Crew", items: staff.filter((member) => !member.is_assigned).map(decorate) }
        ])
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  const handleAdd = (items) => {
    if (!bookingId) return
    dispatch(assignMultipleStaff(bookingId, items.map((item) => ({ profile_id: item.id, role: "crew_member" }))))
  }

  const handleRemove = (item) => {
    // Detail chips carry assignmentId directly; picker-added chips fall back to the
    // fetched assigned list, matching by profile id.
    const assignmentId =
      item.assignmentId ?? assignedCrew.find((a) => a.profileId === item.profileId || a.profileId === item.id)?.id
    if (assignmentId && bookingId) return dispatch(unassignStaffMember(assignmentId, bookingId))
    return undefined
  }

  return (
    <EditableTagCell
      initialItems={initialCrew}
      groups={groups}
      renderLabel={renderCrewLabel}
      addLabel="Add Crew"
      chipClassName={styles.crewLabel}
      onEditStart={handleEditStart}
      onPickerOpen={handlePickerOpen}
      onAdd={handleAdd}
      onRemove={handleRemove}
      loading={isLoading}
      removeLoading={removeLoading}
      emptyLabel={emptyLabel}
      alwaysEditable={alwaysEditable}
      onCountChange={onCountChange}
    />
  )
}

CrewCell.propTypes = {
  initialCrew: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, role: PropTypes.string })),
  bookingId: PropTypes.string,
  emptyLabel: PropTypes.string,
  alwaysEditable: PropTypes.bool,
  onCountChange: PropTypes.func
}

CrewCell.defaultProps = {
  initialCrew: [],
  emptyLabel: "",
  alwaysEditable: false
}

export default CrewCell
